import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { ActivityEntry } from '@garage/shared'
import { Button, Select, Spinner } from '@/ui'
import { api } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useAuth } from '@/components/AuthGate.js'
import { useFormatters } from '@/lib/hooks.js'

const PAGE_SIZE = 50

/** Every entity type the server's activity log can produce — see
 *  lib/activity-log.ts's ENTITY_ROUTES on the server for the source list. */
const ENTITY_TYPES = [
  'jobcard', 'document', 'jobcard_inspection', 'client', 'car', 'catalog_item',
  'supplier', 'worker', 'department', 'staff_event', 'expense', 'expense_import',
  'storage_node', 'storage_allocation', 'settings', 'inspection_template',
  'staff_account', 'system_update',
]

export function ActivityFeedSection() {
  const { account } = useAuth()
  const { t } = useTranslation()
  const formatters = useFormatters()
  const isAdmin = account.role === 'admin'

  const [page, setPage] = useState(1)
  const [entityType, setEntityType] = useState('')
  const [staffAccountId, setStaffAccountId] = useState('')

  const { data: staffList } = useQuery({
    queryKey: qk.staffAccounts,
    queryFn: () => api.staffAccounts.list(),
    enabled: isAdmin,
  })

  const filterParams = {
    page,
    pageSize: PAGE_SIZE,
    entityType: entityType || undefined,
    staffAccountId: staffAccountId || undefined,
  }

  const { data, isLoading } = useQuery({
    queryKey: qk.activityFeed(filterParams),
    queryFn: () => api.activity.feed(filterParams),
    enabled: isAdmin,
  })

  if (!isAdmin) return null

  const items = data?.items ?? []
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1
  const startRow = data && data.total > 0 ? (data.page - 1) * data.pageSize + 1 : 0
  const endRow = data ? Math.min(data.page * data.pageSize, data.total) : 0

  const sentenceFor = (entry: ActivityEntry): string => {
    const typeLabel = t(`activity.entityTypes.${entry.entityType}`, { defaultValue: entry.entityType })
    const label = entry.label ?? t('activity.unnamedRecord')
    return t(`activity.sentence.${entry.action}`, { name: entry.staffDisplayName, type: typeLabel, label })
  }

  return (
    <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
      <h2 className="text-title-md text-on-surface">{t('activity.title')}</h2>
      <p className="text-body-sm text-on-surface-variant mt-0.5">{t('activity.subtitle')}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <Select
          label={t('activity.filterByType')}
          value={entityType}
          onChange={(v: string) => {
            setEntityType(v)
            setPage(1)
          }}
          options={[
            { value: '', label: t('activity.allTypes') },
            ...ENTITY_TYPES.map((et) => ({
              value: et,
              label: t(`activity.entityTypes.${et}`, { defaultValue: et }),
            })),
          ]}
          fullWidth
        />
        <Select
          label={t('activity.filterByStaff')}
          value={staffAccountId}
          onChange={(v: string) => {
            setStaffAccountId(v)
            setPage(1)
          }}
          options={[
            { value: '', label: t('activity.allStaff') },
            ...(staffList?.items ?? []).map((a) => ({ value: a.id, label: a.displayName })),
          ]}
          fullWidth
        />
      </div>

      <div className="mt-4 space-y-2">
        {isLoading ? (
          <Spinner />
        ) : items.length === 0 ? (
          <p className="text-body-sm text-on-surface-variant">{t('activity.noActivity')}</p>
        ) : (
          items.map((entry) => (
            <div
              key={entry.id}
              className="flex items-center justify-between gap-3 p-3 bg-surface-high rounded-md border border-outline-variant"
            >
              <p className="text-body-sm text-on-surface min-w-0">{sentenceFor(entry)}</p>
              <span className="text-body-xs text-on-surface-variant flex-shrink-0 whitespace-nowrap">
                {formatters.dateTime(entry.createdAt)}
              </span>
            </div>
          ))
        )}
      </div>

      {data && data.total > 0 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-body-sm text-on-surface-variant">
            {t('common.showingRange', { from: startRow, to: endRow, total: data.total })}
          </p>
          <div className="flex gap-2">
            <Button variant="outlined" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
              {t('common.previous')}
            </Button>
            <Button
              variant="outlined"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              {t('common.next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
